import { useQuery, UseQueryResult } from 'react-query'
import { useParams } from 'react-router'
import { RouteParameters } from '../../constants'
import { AvailabilityConstraint } from '../../types'
import { logFactory } from '../../util/logFactory'
import { useAppointmentsService } from '../services'
import { createContext, FC, useContext } from 'react'

type UseAvailabilityConstraintContext = Omit<UseQueryResult<AvailabilityConstraint[]>, 'data'> & {
  availabilityConstraints?: AvailabilityConstraint[] | undefined
}

const hookName = 'useAvailabilityConstraint'
const log = logFactory(hookName)

const defaultContext = {
  availabilityConstraints: undefined,
  isError: false,
  isLoading: false,
} as UseAvailabilityConstraintContext

const context = createContext<UseAvailabilityConstraintContext>(defaultContext)

const { Provider } = context

const AvailabilityConstraintsProvider: FC = ({ children }) => {
  const { [RouteParameters.RESOURCE_ID]: resourceId } = useParams()
  const { getAvailabilityConstraints, isReady } = useAppointmentsService()

  const fetchAvailabilityConstraints = async (): Promise<AvailabilityConstraint[]> => {
    log(`🗓️ Fetching availability constraints...`, resourceId)

    const found = await getAvailabilityConstraints({ resource_id: resourceId as string })

    log(`🗓️ Found availability constraints`, found)

    return found
  }

  const isQueryEnabled = Boolean(resourceId && isReady)

  const {
    data: availabilityConstraints,
    isLoading,
    ...results
  } = useQuery([hookName, resourceId], fetchAvailabilityConstraints, {
    enabled: isQueryEnabled,
  })

  const contextValue: UseAvailabilityConstraintContext = {
    ...results,
    availabilityConstraints,
    isLoading: Boolean(!isReady || isLoading),
  }

  return <Provider value={contextValue}>{children}</Provider>
}

const useAvailabilityConstraints = (): UseAvailabilityConstraintContext => useContext(context)

export { AvailabilityConstraintsProvider, useAvailabilityConstraints, hookName as useAvailabilityConstraintsQueryName }
